import { z } from 'zod';
import { emailSchema, nonEmptyText, paginationSchema, passwordSchema } from './common';
import { InstitutionRole, NotificationType, WorkRole } from '../enums';
import { Action } from '../permissions';

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Informe a senha').max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const resetPasswordSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

/** Cadastro aberto: a conta nasce como orientador(a). Sem CPF desde a ADR-0024 (o cadastro pelo Google não o tem). */
export const signupSchema = z.object({
  name: nonEmptyText(160, 'Nome'),
  email: emailSchema,
  password: passwordSchema,
});
export type SignupInput = z.input<typeof signupSchema>;
export type SignupData = z.output<typeof signupSchema>;

/** Pedido de link mágico (lembrete de senha por e-mail). */
export const magicLinkRequestSchema = z.object({ email: emailSchema });
export type MagicLinkRequestInput = z.infer<typeof magicLinkRequestSchema>;

/** Uso do link mágico: entra na conta e, opcionalmente, define uma nova senha. */
export const magicLinkConsumeSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema.optional(),
});
export type MagicLinkConsumeInput = z.infer<typeof magicLinkConsumeSchema>;

/** Recursos públicos de acesso habilitados no servidor (a tela de entrada se adapta). */
export interface AuthConfigDto {
  /** Cadastro aberto de orientadores(as). */
  signup: boolean;
  /** Link mágico por e-mail (exige transporte de e-mail configurado). */
  magicLink: boolean;
  /** Entrar e cadastrar com Google (exige as credenciais do Google). */
  google: boolean;
}

/** Motivos de volta à tela de entrada após o Google (`/entrar?google=<motivo>`). */
export const GOOGLE_LOGIN_ERRORS = {
  cancelado: 'O acesso pelo Google foi cancelado.',
  falhou: 'Não foi possível entrar com o Google. Tente novamente.',
  indisponivel: 'Entrar com Google não está habilitado nesta instalação.',
  'sem-conta': 'Não há conta com este e-mail do Google. Orientandos entram pelo convite do orientador(a).',
  'email-nao-verificado': 'O e-mail desta conta do Google não está verificado.',
  'conta-inativa': 'Esta conta está desativada. Fale com a administração.',
} as const;
export type GoogleLoginError = keyof typeof GOOGLE_LOGIN_ERRORS;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const acceptInvitationSchema = z.object({
  token: z.string().min(20).max(200),
  /** Obrigatório em convites por link (sem e-mail pré-definido) quando não há sessão. */
  email: emailSchema.optional(),
  /** Obrigatórios quando o convite é para um e-mail sem conta. */
  name: nonEmptyText(160, 'Nome').optional(),
  password: passwordSchema.optional(),
});
export type AcceptInvitationInput = z.infer<typeof acceptInvitationSchema>;

export const updateProfileSchema = z.object({
  name: nonEmptyText(160, 'Nome'),
  timezone: z.string().max(64).nullable().optional(),
  /** Cadastro do(a) orientando(a): instituição e programa (texto livre, informativo) */
  institutionName: z.string().trim().max(200).nullable().optional(),
  programName: z.string().trim().max(200).nullable().optional(),
});
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

export const notificationPreferenceSchema = z.object({
  preferences: z
    .array(
      z.object({
        type: z.enum(Object.values(NotificationType) as [NotificationType, ...NotificationType[]]),
        inApp: z.boolean(),
      }),
    )
    .max(50),
});
export type NotificationPreferenceInput = z.infer<typeof notificationPreferenceSchema>;

/** Anonimização de conta a pedido do titular (superadministração). */
/** Papéis do produto, como abas da gestão de usuários (ADMIN = superadministração). Os papéis institucionais legados não têm aba. */
export const ADMIN_USER_ROLES = ['ADMIN', 'ADVISOR', 'STUDENT'] as const;
export type AdminUserRole = (typeof ADMIN_USER_ROLES)[number];
export const ADMIN_USER_ROLE_LABELS: Record<AdminUserRole, string> = {
  ADMIN: 'Administradores',
  ADVISOR: 'Orientadores',
  STUDENT: 'Orientandos',
};

export const adminUsersQuerySchema = paginationSchema.extend({
  q: z.string().trim().max(120).optional(),
  /** Só as contas com este papel (ausente = todas) */
  role: z.enum(ADMIN_USER_ROLES).optional(),
});
export type AdminUsersQuery = z.infer<typeof adminUsersQuerySchema>;

export interface SessionMembership {
  institutionId: string;
  institutionName: string;
  acronym: string;
  timezone: string;
  roles: { role: InstitutionRole; programId: string | null; programName: string | null }[];
  /** Espaço de orientação pessoal (não é uma instituição cadastrada) */
  isWorkspace: boolean;
  /** Ações institucionais permitidas (ex.: policy.manage) — apenas para a UI. */
  actions: Action[];
}

export interface SessionDto {
  user: {
    id: string;
    email: string;
    name: string;
    isSuperAdmin: boolean;
    timezone: string | null;
    mustChangePassword: boolean;
    institutionName: string | null;
    programName: string | null;
    isDemo: boolean;
    /** CPF mascarado para exibição ("***.456.789-**"); null quando a conta não tem CPF (contas criadas antes da ADR-0024 podem ter) */
    cpfMasked: string | null;
    /** Foto do perfil (null = iniciais) */
    avatarUrl: string | null;
  };
  /** "Ver como": sessão aberta pela superadministração em nome desta conta */
  impersonator: { id: string; name: string } | null;
  /** Identifica este login (ex.: o aviso da primeira tela aparece uma vez por login) */
  sessionId: string;
  csrfToken: string;
  memberships: SessionMembership[];
  /** Papéis do usuário em trabalhos, agregados (para escolher o menu). */
  workRoles: WorkRole[];
  /** Perfil de navegação sugerido para a UI. */
  persona: 'admin' | 'advisor' | 'student';
  features: { ai: boolean };
  /** Orientação: pode convidar orientandos; vínculos ativos como orientando(a) */
  advising: { canAdvise: boolean; activeLinksAsStudent: number };
}
